/* eslint-disable react-hooks/exhaustive-deps */
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { isEmpty, upperFirst } from 'lodash';
import { useForm, SubmitHandler } from 'react-hook-form';
import { CloseIcon, DeleteIcon, DownIcon, UpIcon } from '../../../shared/components/AppIcons';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid2,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
} from '@mui/material';
import RichTextEditorComponent from '../../../shared/components/RichTextEditor';
import ThemeService from '../services/theme.services';
import { TagType } from '../../../../data/types/tags.interface';
import theme from '../../../../core/theme';
import Toaster from '../../../shared/components/Toaster';

const useStyles = makeStyles({
  paper: {
    minWidth: '412px',
    height: 'auto',
    borderRadius: '4px !important',
    boxShadow: 'rgba(242, 242, 242, 1)',
  },
  header: {
    fontSize: '28px',
  },
  close: {
    fontSize: '50px !important',
    color: '#5F6774 !important',
    '& path': {
      fill: `#2C2C2C !important`,
    },
  },
  input: {
    padding: '8px !important',
    height: '40px',
    border: '1px solid #E1E3E8',
  },
  icon: {
    fontSize: '24px !important',
    '& path': {
      fill: `#F67570 !important`,
    },
  },
  save: {
    height: '38px',
    fontSize: '18px !important',
    boxShadow: 'none !important',
    backgroundColor: '#F67570 !important',
    color: '#fff !important',
    '& g path': {
      fill: '#ffffff !important',
    },
    '&.disable': {
      backgroundColor: `${theme.palette.chipBg.main} !important`,
    },
  },
  cancel: {
    color: '#F67570 !important',
    marginRight: '24px !important',
    fontSize: '16px !important',
    fontFamily: 'Lato !important',
  },
  list: {
    margin: '11px 0px 11px 0px',
    '& .MuiStack-root': {
      border: '1px solid #E1E3E8',
      borderRadius: '4px',
      height: '40px !important',
    },
    '& .MuiInput-underline:before': {
      borderBottom: 'none',
    },
    '& .MuiInput-underline:after': {
      borderBottom: 'none',
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled, .Mui-error):before': {
      borderBottom: 'none',
    },
  },
  type: {
    '&.MuiFormControl-root': {
      margin: '0px',
      backgroundColor: '#fff',
      '& .MuiInputBase-root': {
        border: '0px solid #E1E3E8',
        borderRadius: '4px',
        height: '40px !important',
        fontFamily: 'Lato !important',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E1E3E8 !important',
    },
    // Border on hover
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E1E3E8 !important',
    },
    // Border on focus
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E1E3E8 !important',
      borderWidth: '1px !important',
    },
    // Remove native input outline
    '& input': {
      outline: 'none !important',
    },
  },
  dropdownIcon: {
    fontSize: '42px !important',
    '& path': {
      fill: '#5F6774 !important',
    },
  },
});

interface Props {
  setOpenPopup: Dispatch<SetStateAction<boolean>>;
  tagType: string;
}

const AddTheme: React.FC<Props> = ({ setOpenPopup, tagType }) => {
  const classes = useStyles();
  const { register, handleSubmit } = useForm({ mode: 'all' });
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [themeList, setThemeList] = useState<TagType[]>([]);
  const [topicList, setTopicList] = useState<TagType[]>([]);
  const [subTopicList, setSubTopicList] = useState<TagType[]>([]);
  const [selectedThemes, setSelectedThemes] = useState<TagType[]>([]);
  const [selectedTopics, setSelectedTopics] = useState<TagType[]>([]);
  const [selectedSubTopics, setSelectedSubTopics] = useState<TagType[]>([]);
  const [name, setName] = useState<string>('');
  const [type, setType] = useState<string>('theme');
  const [description, setDescription] = useState('');
  const [belongsTo, setBelongsTo] = useState<string>('');
  const [open, setOpen] = useState(false);
  const [show, setShow] = useState<boolean>(false);
  const [toasterMessage, setToasterMessage] = useState('');

  const getToaster = () => <Toaster messageType="remove" show={show} setShow={setShow} message={`${toasterMessage}`} />;

  const defaultThemeProps = {
    options: themeList,
    getOptionLabel: (option: TagType) => option.name,
  };

  const defaultTopicProps = {
    options: topicList.filter((topic) => themeList.map((theme) => theme.id).includes(topic?.parentThemeId || 0)),
    getOptionLabel: (option: TagType) => option.name,
  };

  const defaultSubTopicProps = {
    options: subTopicList,
    getOptionLabel: (option: TagType) => option.name,
  };

  const getAllThemes = async (type: string, theme?: TagType[], topic?: TagType[]) => {
    const result = await ThemeService.get(type, theme, topic);
    if (type === 'theme') {
      setThemeList(result);
    }
    if (type === 'topic') {
      setTopicList(result);
    }
    if (type === 'sub-topic') {
      setSubTopicList(result);
    }
  };

  useEffect(() => {
    getAllThemes('theme');
    getAllThemes('topic');
    getAllThemes('sub-topic');
    setType(tagType);
  }, []);

  const onSubmit: SubmitHandler<any> = async (form) => {
    if (!name) {
      setErrorMessage('Please enter the name');
    }
    try {
      const data = { name, type, description, belongsTo };
      const content = await ThemeService.create(data);
      if (content) {
        setOpenPopup(false);
      }
    } catch (error) {
      setErrorMessage(error?.response?.data[0]?.message || 'Please try again');
    }
  };

  const handleChange = (e: SelectChangeEvent) => {
    setType(e.target.value);
    setErrorMessage('');
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setErrorMessage('');
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
    setErrorMessage('');
  };

  const handleThemeChange = (e: React.SyntheticEvent<Element, Event>, values: TagType[]) => {
    setSelectedThemes(values);
    getAllThemes('topic', values);
  };

  const handleTopicChange = (e: React.SyntheticEvent<Element, Event>, values: TagType[]) => {
    setSelectedTopics(values);
    getAllThemes('sub-topic', [], values);
  };

  const handleSubTopicChange = (e: React.SyntheticEvent<Element, Event>, values: TagType[]) => {
    setSelectedSubTopics(values);
  };

  const handleBelongsToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBelongsTo(e.target.value);
    setErrorMessage('');
  };

  const handleDelete = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, option: TagType) => {
    const isDeleted = await ThemeService.deleteThemeOrTopicOrSubTopics([option.id], option);
    if (isDeleted) {
      setShow(true);
      setToasterMessage('Deleted successfully');
    }
  };

  const disableButton = () => !name || !type || (type !== 'theme' && !belongsTo);

  return (
    <Dialog
      open
      aria-modal="true"
      role="dialog"
      onClose={() => setOpenPopup(false)}
      PaperProps={{
        onClick: (e: any) => e.stopPropagation(),
        className: classes.paper,
      }}
    >
      <DialogTitle>
        <Grid2 container justifyContent="space-between" alignItems="center">
          <Grid2>
            <span className={classes.header}>{`Add ${upperFirst(type)}`}</span>
          </Grid2>
          <Grid2 container justifyContent="flex-end">
            <IconButton onClick={() => setOpenPopup(false)} role="button" sx={{ padding: 0 }}>
              <CloseIcon viewBox="0 0 42 42" className={classes.close} />
            </IconButton>
          </Grid2>
        </Grid2>
        <Grid2>
          <Divider
            orientation="horizontal"
            flexItem
            sx={{
              backgroundColor: '#E1E3E8',
              paddingBottom: '1px',
            }}
          />
        </Grid2>
      </DialogTitle>
      <DialogContent sx={{ paddingBottom: '0px' }}>
        {show && getToaster()}
        <form onSubmit={handleSubmit(onSubmit)}>
          {errorMessage && errorMessage.length > 0 && (
            <Grid2 container justifyContent="center">
              <div className="text-danger mb-1 pb-5 text-center">{errorMessage}</div>
            </Grid2>
          )}
          <Grid2 container direction="column">
            <Grid2 container direction="column">
              <label>Name*</label>
              <input
                {...register('name', { required: true })}
                name="name"
                id="name"
                className={classes.input}
                onChange={(e) => handleNameChange(e)}
              />
            </Grid2>

            <Grid2 container direction="column" mt={2}>
              <label>Type*</label>

              <FormControl sx={{ m: 1, minWidth: 120, height: '40px' }} className={classes.type}>
                <Select
                  value={type}
                  onChange={handleChange}
                  displayEmpty
                  readOnly={!isEmpty(tagType)}
                  IconComponent={open ? UpIcon : DownIcon}
                >
                  <MenuItem key={0} value="theme">
                    Theme
                  </MenuItem>
                  <MenuItem key={1} value="topic">
                    Topic
                  </MenuItem>
                  <MenuItem key={2} value="sub-topic">
                    Sub-Topic
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid2>

            {type === 'theme' && (
              <Grid2 container mt={2} direction="column" className={classes.list}>
                <label>Existing Theme</label>
                <Stack spacing={1}>
                  <Autocomplete
                    {...defaultThemeProps}
                    id="auto-highlight"
                    autoHighlight
                    multiple
                    value={selectedThemes}
                    onChange={handleThemeChange}
                    onClose={() => setOpen(false)}
                    popupIcon={
                      open ? <UpIcon className={classes.dropdownIcon} /> : <DownIcon className={classes.dropdownIcon} />
                    }
                    renderInput={(params) => <TextField {...params} label="" variant="standard" />}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                          <span>{option.name}</span>

                          <IconButton size="small" onClick={(e) => handleDelete(e, option)}>
                            <DeleteIcon viewBox="3 5 24 24" className={classes.icon} style={{ cursor: 'pointer' }} />
                          </IconButton>
                        </Stack>
                      </li>
                    )}
                  />
                </Stack>
              </Grid2>
            )}

            {type === 'topic' && (
              <Grid2 container mt={2} direction="column" className={classes.list}>
                <label>Existing Topic</label>
                <Stack spacing={1}>
                  <Autocomplete
                    {...defaultTopicProps}
                    id="auto-highlight"
                    autoHighlight
                    multiple
                    value={selectedTopics}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    popupIcon={
                      open ? <UpIcon className={classes.dropdownIcon} /> : <DownIcon className={classes.dropdownIcon} />
                    }
                    onChange={handleTopicChange}
                    renderInput={(params) => <TextField {...params} label="" variant="standard" />}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                          <span>{option.name}</span>

                          <IconButton size="small" onClick={(e) => handleDelete(e, option)}>
                            <DeleteIcon viewBox="3 5 24 24" className={classes.icon} style={{ cursor: 'pointer' }} />
                          </IconButton>
                        </Stack>
                      </li>
                    )}
                  />
                </Stack>
              </Grid2>
            )}

            {type === 'sub-topic' && (
              <Grid2 container mt={2} direction="column" className={classes.list}>
                <label>Existing Sub Topic</label>
                <Stack spacing={1}>
                  <Autocomplete
                    {...defaultSubTopicProps}
                    id="auto-highlight"
                    autoHighlight
                    multiple
                    value={selectedSubTopics}
                    onChange={handleSubTopicChange}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    popupIcon={
                      open ? <UpIcon className={classes.dropdownIcon} /> : <DownIcon className={classes.dropdownIcon} />
                    }
                    renderInput={(params) => <TextField {...params} label="" variant="standard" />}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                          <span>{option.name}</span>

                          <IconButton size="small" onClick={(e) => handleDelete(e, option)}>
                            <DeleteIcon viewBox="3 5 24 24" className={classes.icon} style={{ cursor: 'pointer' }} />
                          </IconButton>
                        </Stack>
                      </li>
                    )}
                  />
                </Stack>
              </Grid2>
            )}

            <Grid2 container direction="column" mt={2}>
              <label>Short Description</label>
              <RichTextEditorComponent description={description} handleDescriptionChange={handleDescriptionChange} />
            </Grid2>

            {type && type !== 'theme' && (
              <Grid2 container direction="column" mt={2}>
                <label>{`Assign Under ${type === 'sub-topic' ? 'Topic' : 'Theme'}*`}</label>
                <input
                  {...register('belongsTo', { required: type === 'sub-topic' })}
                  name="belongsTo"
                  id="belongsTo"
                  className={classes.input}
                  onChange={(e) => handleBelongsToChange(e)}
                />
              </Grid2>
            )}
          </Grid2>
          <Grid2 container justifyContent="end" m={2} mr={0}>
            <Button
              size="small"
              variant="text"
              type="button"
              onClick={() => setOpenPopup(false)}
              className={classes.cancel}
            >
              cancel
            </Button>
            <Button
              size="small"
              color="primary"
              variant="contained"
              className={`${classes.save} ${!name ? 'disable' : ''}`}
              type="submit"
              disabled={disableButton() || !isEmpty(errorMessage)}
            >
              <div className="caption" style={{ fontSize: 'h5', textTransform: 'none' }}>
                Save
              </div>
            </Button>
          </Grid2>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddTheme;
